import {
  Alert,
  Button,
  Chip,
  Group,
  Progress,
  Stack,
  Text,
  Tile,
  Title,
} from "@assetwatch/ui";
import { type ReactNode, type RefObject, useEffect, useState } from "react";
import {
  ProcedureExecutionItemCard,
  type ProcedurePhotoFieldValidation,
} from "../snowflakes/work-orders/ProcedureExecutionItemCard";
import { ProcedureSectionDisclosure } from "../snowflakes/work-orders/ProcedureSectionDisclosure";
import type { ProcedureItemAutosave } from "./useProcedureItemAutosave";

export type ProcedureItemType =
  | "text"
  | "checkbox"
  | "numeric"
  | "single-choice"
  | "photo";

export type ProcedureExecutionItem = {
  id: string;
  label: string;
  type: ProcedureItemType;
  required?: boolean;
  description?: string;
  options?: string[];
  /** Unit of a numeric step, shown with its field. */
  unit?: string;
};

/**
 * One structural entry of the attached Procedure, in display order. A
 * `heading` entry groups the sections that follow it through `headingId`;
 * headings and sections never accept a response themselves, only their items
 * do.
 */
export type ProcedureExecutionSection = {
  id: string;
  label: string;
  kind?: "heading";
  /** Id of the heading entry this section belongs to. */
  headingId?: string;
  /** Ids of every section above this one, outermost first. */
  ancestorIds?: string[];
  items: ProcedureExecutionItem[];
};

export type ProcedureExecutionData = {
  id?: string;
  title: string;
  heading?: string;
  versionLabel?: string;
  status?: "not-started" | "in-progress" | "completed";
  completedAt?: string;
  completedBy?: string;
  savedAnswers?: Answers;
  responseHistory?: ProcedureResponseHistoryEntry[];
  sections: ProcedureExecutionSection[];
};

export type ProcedureResponseHistoryEntry = {
  itemId: string;
  itemLabel: string;
  value: string;
  updatedAt: string;
  updatedBy: string;
};

export type ProcedureExecutionState =
  | "ready"
  | "loading"
  | "empty"
  | "error"
  | "unauthorized"
  | "unavailable";

export type ProcedureAnswer = string | boolean | number | string[] | undefined;
type Answers = Record<string, ProcedureAnswer>;

/**
 * An answer reduced to what is compared: a blank answer is no answer, and text
 * is compared trimmed because that is what is stored.
 */
function comparableAnswer(answer: ProcedureAnswer) {
  if (answer === undefined) return undefined;
  if (typeof answer === "boolean") return answer;
  const text = (
    Array.isArray(answer) ? answer.join(",") : String(answer)
  ).trim();
  return text === "" ? undefined : text;
}

function sameAnswer(left: ProcedureAnswer, right: ProcedureAnswer) {
  return comparableAnswer(left) === comparableAnswer(right);
}

function flattenItems(procedure: ProcedureExecutionData) {
  return procedure.sections.flatMap((section) => section.items);
}

/**
 * A PHOTO item counts only when the server has saved at least one photo for
 * it, so photos still uploading, saving or failed never satisfy it. Without
 * server photo state (a static fixture), the fixture's own answers are used.
 */
function itemIsAnswered(
  item: ProcedureExecutionItem,
  answers: Answers,
  savedPhotoAnswers?: Record<string, string[]>,
) {
  if (item.type === "photo" && savedPhotoAnswers)
    return (savedPhotoAnswers[item.id]?.length ?? 0) > 0;
  const answer = answers[item.id];
  if (answer === undefined || answer === "") return false;
  if (Array.isArray(answer)) return answer.length > 0;
  if (item.type === "checkbox") return answer === true;
  if (item.type === "text")
    return typeof answer === "string" && answer.trim().length > 0;
  if (item.type === "numeric") {
    const numericAnswer =
      typeof answer === "number"
        ? answer
        : typeof answer === "string"
          ? Number(answer)
          : Number.NaN;
    return Number.isFinite(numericAnswer);
  }
  return item.type !== "photo";
}

function formatResponseDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function responseStatusLabel(status: ProcedureExecutionData["status"]) {
  if (status === "completed") return "Completed";
  if (status === "in-progress") return "In progress";
  return "Not started";
}

function StateMessage({
  onRetry,
  state,
}: {
  onRetry?: () => void;
  state: Exclude<ProcedureExecutionState, "ready">;
}) {
  const messages = {
    loading: {
      title: "Loading Procedure",
      body: "The Procedure execution snapshot is loading.",
      tone: "neutral" as const,
    },
    empty: {
      title: "No Procedure steps",
      body: "This attached Procedure does not have any executable steps.",
      tone: "neutral" as const,
    },
    error: {
      title: "Unable to load Procedure",
      body: "The Procedure execution snapshot could not be loaded.",
      tone: "negative" as const,
    },
    unauthorized: {
      title: "Procedure unavailable",
      body: "You do not have permission to execute this Procedure.",
      tone: "negative" as const,
    },
    unavailable: {
      title: "No Procedure attached",
      body: "Procedure execution is unavailable until an execution snapshot is attached to this Work Order.",
      tone: "neutral" as const,
    },
  }[state];

  return (
    <Alert
      action={
        state === "error" && onRetry ? (
          <Button onClick={onRetry} size="sm" variant="outline">
            Retry
          </Button>
        ) : undefined
      }
      subtext={messages.body}
      title={messages.title}
      tone={messages.tone}
    />
  );
}

/**
 * Saves every unsaved answer and waits for saves in flight; rejects when an
 * answer could not be saved. Set only while something is unsaved.
 */
export type PendingProcedureSave = () => Promise<void>;

export function ProcedureExecutionLayout({
  initialAnswers = {},
  itemSave,
  onRetry,
  pendingSaveRef,
  procedure,
  state = "unavailable",
  readOnly = false,
  instructions,
  onComplete,
  photoField,
  savedPhotoAnswers,
  workOrderNumber,
  workOrderTitle,
}: {
  /** The answers the server has saved. */
  initialAnswers?: Answers;
  /**
   * Saves each answer as the user works. Without it the answers stay local to
   * the page, as in a static fixture.
   */
  itemSave?: ProcedureItemAutosave;
  onRetry?: () => void;
  /**
   * Receives a function that saves unsaved answers, so a parent can save them
   * before completing the Work Order. Holds null when nothing is unsaved.
   */
  pendingSaveRef?: RefObject<PendingProcedureSave | null>;
  procedure?: ProcedureExecutionData;
  state?: ProcedureExecutionState;
  readOnly?: boolean;
  instructions?: string | null;
  onComplete?: () => void;
  /** Renders the response control for a PHOTO item. */
  photoField?: (
    item: ProcedureExecutionItem,
    validation: ProcedurePhotoFieldValidation,
  ) => ReactNode;
  /** Server-saved photo ids per PHOTO item; only these count as answered. */
  savedPhotoAnswers?: Record<string, string[]>;
  workOrderNumber?: string;
  workOrderTitle?: string;
}) {
  const savedAnswers =
    initialAnswers && Object.keys(initialAnswers).length > 0
      ? initialAnswers
      : (procedure?.savedAnswers ?? {});
  const executionIdentity = `${workOrderNumber ?? ""}:${procedure?.id ?? ""}:${procedure?.versionLabel ?? ""}`;
  // Edits the server has not confirmed yet, by item id. Everything else shows
  // the saved answer, so a save that finishes for one item never overwrites
  // text being typed into another.
  const [draftState, setDraftState] = useState<{
    executionIdentity: string;
    drafts: Answers;
  }>({ executionIdentity, drafts: {} });
  const drafts =
    draftState.executionIdentity === executionIdentity ? draftState.drafts : {};
  const answers: Answers = { ...savedAnswers, ...drafts };
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const items = procedure ? flattenItems(procedure) : [];
  const answeredCount = items.filter((item) =>
    itemIsAnswered(item, answers, savedPhotoAnswers),
  ).length;
  // Completion is checked on the server against saved responses, so the
  // validation message counts saved answers, not local edits.
  // Only Required steps block completion; Optional steps may stay unanswered.
  const incompleteSteps = items.filter(
    (item) =>
      item.required && !itemIsAnswered(item, savedAnswers, savedPhotoAnswers),
  ).length;
  const dirtyItems = items.filter(
    (item) =>
      item.id in drafts && !sameAnswer(drafts[item.id], savedAnswers[item.id]),
  );
  const hasUnsavedAnswers =
    dirtyItems.length > 0 || Boolean(itemSave?.hasPending);
  const needsFlush = Boolean(itemSave) && !readOnly && hasUnsavedAnswers;
  const progress = items.length ? (answeredCount / items.length) * 100 : 0;
  const procedureStatus = procedure?.status ?? "not-started";
  // A ready Procedure with no steps has nothing to validate; show the empty
  // state rather than reporting all Procedure steps as complete.
  const displayState: ProcedureExecutionState =
    state === "ready" && procedure && items.length === 0 ? "empty" : state;

  // An edit not yet sent (the field never lost focus) goes first, then every
  // save in flight is awaited. The effect below republishes the function after
  // every render because it reads that render's drafts.
  async function flushDrafts() {
    for (const item of dirtyItems) commitAnswer(item, drafts[item.id]);
    await itemSave?.flushAll();
  }

  useEffect(() => {
    if (!pendingSaveRef) return;
    pendingSaveRef.current = needsFlush ? flushDrafts : null;
    return () => {
      pendingSaveRef.current = null;
    };
  });

  function toggleSection(id: string) {
    setCollapsed((current) => ({ ...current, [id]: !current[id] }));
  }

  function setDraft(itemId: string, answer: ProcedureAnswer) {
    setDraftState((current) => ({
      executionIdentity,
      drafts: {
        ...(current.executionIdentity === executionIdentity
          ? current.drafts
          : {}),
        [itemId]: answer,
      },
    }));
  }

  // Once an answer is stored it is shown from the saved answers again, unless
  // the user has typed something newer since.
  function dropDraftIfUnchanged(itemId: string, committed: ProcedureAnswer) {
    setDraftState((current) => {
      if (
        !(itemId in current.drafts) ||
        !sameAnswer(current.drafts[itemId], committed)
      )
        return current;
      const { [itemId]: _stored, ...rest } = current.drafts;
      return { ...current, drafts: rest };
    });
  }

  function commitAnswer(item: ProcedureExecutionItem, answer: ProcedureAnswer) {
    if (!itemSave || readOnly) return;
    // A failure is shown on the item; the draft stays so nothing typed is lost.
    itemSave.commit(item, answer).then(
      () => dropDraftIfUnchanged(item.id, answer),
      () => undefined,
    );
  }

  function changeAnswer(
    item: ProcedureExecutionItem,
    value: string | boolean | string[],
  ) {
    setDraft(item.id, value);
    // A checkbox or a choice is a single act, so it is saved at once. Text and
    // numbers are saved when the field loses focus.
    if (item.type === "checkbox" || item.type === "single-choice")
      commitAnswer(item, value);
  }

  function keepMine(item: ProcedureExecutionItem) {
    const answer = drafts[item.id];
    itemSave?.keepMine(item, answer).then(
      () => dropDraftIfUnchanged(item.id, answer),
      () => undefined,
    );
  }

  function acceptTheirs(item: ProcedureExecutionItem) {
    setDraftState((current) => {
      const { [item.id]: _discarded, ...rest } = current.drafts;
      return { ...current, drafts: rest };
    });
    itemSave?.acceptTheirs(item.id);
  }

  return (
    <Stack gap={16}>
      <Group align="start" justify="between" wrap="wrap">
        <Stack gap={4}>
          <Title as="h2" size="lg">
            {procedure?.title ?? "Procedure execution"}
          </Title>
          <Group gap={8} wrap="wrap">
            <Text size="sm" tone="subtle">
              {workOrderNumber && workOrderTitle
                ? `${workOrderNumber} · ${workOrderTitle}`
                : "Work Order context"}
            </Text>
            {procedure?.versionLabel ? (
              <Chip label={procedure.versionLabel} tone="neutral" />
            ) : null}
            {procedure ? (
              <Chip
                label={responseStatusLabel(procedureStatus)}
                tone={
                  procedureStatus === "completed" ? "positive" : "informative"
                }
              />
            ) : null}
          </Group>
        </Stack>
        {displayState === "ready" ? (
          readOnly ? (
            <Text size="sm" tone="subtle">
              Locked · read-only
            </Text>
          ) : (
            <Group align="center" gap={8} wrap="nowrap">
              <div className="w-[min(160px,30vw)]">
                <Progress aria-label="Procedure progress" value={progress} />
              </div>
              <Text size="sm" tone="subtle">
                {answeredCount} of {items.length}
              </Text>
            </Group>
          )
        ) : null}
      </Group>

      {displayState === "ready" && procedure ? (
        <>
          <Group align="center" justify="between" wrap="wrap">
            <Text size="sm" tone="subtle">
              {answeredCount} of {items.length} executable items answered
            </Text>
            {procedureStatus === "completed" && procedure?.completedAt ? (
              <Text size="sm" tone="subtle">
                Completed {formatResponseDate(procedure.completedAt)}
                {procedure.completedBy ? ` by ${procedure.completedBy}` : ""}
              </Text>
            ) : null}
          </Group>
          <div className="flex flex-col gap-(--spacing-20)">
            <ProcedureSectionDisclosure
              expanded={!collapsed.procedure}
              label={procedure.heading ?? "Procedure steps"}
              onToggle={() => toggleSection("procedure")}
              variant="root"
            />
            {!collapsed.procedure ? (
              <div className="flex flex-col gap-(--spacing-16)">
                {procedure.sections.map((section) =>
                  section.ancestorIds?.some((id) => collapsed[id]) ? null : (
                    <div key={section.id}>
                      <ProcedureSectionDisclosure
                        expanded={!collapsed[section.id]}
                        label={section.label}
                        onToggle={() => toggleSection(section.id)}
                        variant={
                          section.kind === "heading" ? "root" : "section"
                        }
                      />
                      {!collapsed[section.id] && section.items.length > 0 ? (
                        <div className="flex flex-col gap-(--spacing-8)">
                          {section.items.map((item) => (
                            <ProcedureExecutionItemCard
                              answer={answers[item.id]}
                              item={item}
                              key={item.id}
                              onBlur={() =>
                                item.id in drafts
                                  ? commitAnswer(item, drafts[item.id])
                                  : undefined
                              }
                              onChange={(value) => changeAnswer(item, value)}
                              onKeepMine={() => keepMine(item)}
                              onRetry={() =>
                                commitAnswer(item, drafts[item.id])
                              }
                              onUseTheirs={() => acceptTheirs(item)}
                              photoField={
                                item.type === "photo" && photoField
                                  ? (validation) => photoField(item, validation)
                                  : undefined
                              }
                              readOnly={readOnly}
                              status={itemSave?.statusFor(item.id)}
                              unresolved={
                                Boolean(item.required) &&
                                !itemIsAnswered(
                                  item,
                                  answers,
                                  savedPhotoAnswers,
                                )
                              }
                            />
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ),
                )}
              </div>
            ) : null}
          </div>
          <Tile padding="compact" width="full">
            <Group align="center" justify="between" wrap="wrap">
              <Stack gap={4}>
                <Text weight="medium">Completion validation</Text>
                <Text size="sm" tone="subtle">
                  {incompleteSteps > 0
                    ? `${incompleteSteps} required step${incompleteSteps === 1 ? "" : "s"} remaining.`
                    : "All required steps are complete."}
                </Text>
              </Stack>
              {onComplete && !readOnly ? (
                <Button
                  disabled={
                    readOnly || incompleteSteps > 0 || hasUnsavedAnswers
                  }
                  onClick={onComplete}
                >
                  Complete Procedure
                </Button>
              ) : null}
            </Group>
          </Tile>
          {procedure.responseHistory && procedure.responseHistory.length > 0 ? (
            <Tile padding="compact" width="full">
              <Stack gap={8}>
                <Text weight="medium">Response history</Text>
                <div className="divide-y divide-outline-subtle">
                  {procedure.responseHistory.map((entry) => (
                    <div
                      className="flex flex-wrap items-baseline justify-between gap-(--spacing-8) py-(--spacing-8) first:pt-0 last:pb-0"
                      key={`${entry.itemId}:${entry.updatedAt}`}
                    >
                      <Stack gap={4}>
                        <Text size="sm" weight="medium">
                          {entry.itemLabel}
                        </Text>
                        <Text size="sm">{entry.value}</Text>
                      </Stack>
                      <Text size="sm" tone="subtle">
                        {entry.updatedBy} ·{" "}
                        {formatResponseDate(entry.updatedAt)}
                      </Text>
                    </div>
                  ))}
                </div>
              </Stack>
            </Tile>
          ) : null}
        </>
      ) : (
        <Stack gap={12}>
          <StateMessage
            onRetry={onRetry}
            state={displayState === "ready" ? "unavailable" : displayState}
          />
          {instructions ? <Text>{instructions}</Text> : null}
        </Stack>
      )}
    </Stack>
  );
}
